/* istanbul ignore file */
import i18next, { init } from '@ichef/fe-i18n';
import { localStorageUtils } from '@ichef/fe-utils';
import { DEFAULT_LOCALE, ZH_TW, EN } from 'shared/constants/locales';
// 移除此 logException import 之後，記得要把 setupTest 的 @sentry/browser mock 拿掉
import { logException } from 'shared/utils/sentry';

// Webpack will give us the location of the translation files
import enTranslationFile from '../../../locales/en/translation.json';
import twTranslationFile from '../../../locales/zh_TW/translation.json';

const translationUrl = {
  [ZH_TW]: twTranslationFile,
  [EN]: enTranslationFile,
};

export const KEY_LOCALE = 'locale';

const reZh = /^zh/;
const isZhLanguageFamily = (lng) => reZh.test(lng);

const reEn = /^en/;
const isEnLanguageFamily = (lng) => reEn.test(lng);

export const regionKeyMapping = {
  TW: ZH_TW,
  HK: ZH_TW,
  MY: EN,
  SG: EN,
};

export function getCurrentLocale() {
  const localeCache = localStorageUtils.getItem(KEY_LOCALE);
  if (localeCache) {
    return localeCache;
  }

  // Get locale code from client navigator.
  const clientLanguage = window?.navigator?.language;
  if (clientLanguage) {
    if (isZhLanguageFamily(clientLanguage)) {
      return ZH_TW;
    }

    if (isEnLanguageFamily(clientLanguage)) {
      return EN;
    }
  }

  // set locale to EN as default, if language is not matched above.
  return DEFAULT_LOCALE;
}

// ref: https://github.com/iCHEF/iCHEF-online-restaurant/pull/1171
const nsSeparator = '\uE000';
/**
 * Initialize i18next
 */
export const initializeI18n = async () => {
  const locale = getCurrentLocale();
  localStorageUtils.setItem(KEY_LOCALE, locale);

  const resources = {};

  if (process.env.NODE_ENV !== 'test') {
    try {
      // 第一次嘗試
      // Dynamic import the translation files based on user's selected language
      const translationResponse = await fetch(translationUrl[locale]);
      const translationJson = await translationResponse.json();
      resources[locale] = { translation: translationJson };
    } catch (firstError) {
      // 第一次失敗就記錄到 Sentry
      logException(firstError, {
        tag: {
          type: 'i18n_error',
          action: 'fetch_translation_first_attempt',
        },
        context: {
          locale,
          url: translationUrl[locale],
          errorName: firstError.name,
          errorMessage: firstError.message,
        },
      });

      try {
        // 立即重試一次
        const retryResponse = await fetch(translationUrl[locale]);
        const retryJson = await retryResponse.json();
        resources[locale] = { translation: retryJson };
      } catch (retryError) {
        // 重試也失敗，記錄到 Sentry 並使用預設值
        logException(retryError, {
          tag: {
            type: 'i18n_error',
            action: 'fetch_translation_retry_failed',
          },
          context: {
            locale,
            url: translationUrl[locale],
            errorName: retryError.name,
            errorMessage: retryError.message,
          },
        });
      }
    }
  }

  await init({
    lng: locale,
    resources,
    fallbackLng: ZH_TW,
    nsSeparator,
  });
};

// eslint-disable-next-line import/prefer-default-export
export const changeLanguage = (lng) => {
  localStorageUtils.setItem(KEY_LOCALE, lng);

  // return a promise
  i18next.changeLanguage(lng);

  // reload language on i18next v7
  window.location.reload();
};
